import { BREAKING_NEWS_GTM_EVENT } from "./events";

export type BreakingNewsEvent = CustomEvent<{
  element: HTMLElement;
}>;

export class BreakingNews extends HTMLElement {
  #breakingNewsElement: HTMLElement | null = null;
  #breakingNewsInnerElement: HTMLElement | null = null;
  #breakingNewsContentElement: HTMLElement | null = null;
  #breakingNewsContentElementCopy: HTMLElement | null = null;
  #animationDurationInMs = 15000;
  #animationRepeat = 3;
  #resetTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    super();
  }

  connectedCallback() {
    this.#breakingNewsElement = this.querySelector(".breaking-news");
    this.#breakingNewsInnerElement = this.querySelector(
      ".breaking-news__inner",
    );
    this.#breakingNewsContentElement = this.querySelector(
      ".breaking-news__content",
    );
    if (this.#breakingNewsElement && this.#breakingNewsInnerElement) {
      this.#dispatchReadyEvent();
      this.#checkForAnimation();
    }
  }

  disconnectedCallback() {
    this.#cancelAll();
  }

  #dispatchReadyEvent = () => {
    if (!this.#breakingNewsElement) return;

    const event: BreakingNewsEvent = new CustomEvent(BREAKING_NEWS_GTM_EVENT, {
      detail: {
        element: this.#breakingNewsElement,
      },
    });
    document.dispatchEvent(event);
  };

  #checkForAnimation = () => {
    if (!this.#breakingNewsElement) return;

    if (
      this.#breakingNewsElement.scrollWidth >
        this.#breakingNewsElement.clientWidth &&
      this.#canAnimate()
    ) {
      this.#addAnimation();
    }
  };

  #canAnimate = () => {
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    const motionOK = window.matchMedia(
      "(prefers-reduced-motion: no-preference)",
    ).matches;
    return isMobile && motionOK;
  };

  #addAnimation = () => {
    if (
      !this.#breakingNewsElement ||
      !this.#breakingNewsInnerElement ||
      !this.#breakingNewsContentElement
    )
      return;

    this.#breakingNewsContentElementCopy =
      this.#breakingNewsContentElement.cloneNode(true) as HTMLElement;
    // Hide duplicated text for screen reader
    this.#breakingNewsContentElementCopy.setAttribute("aria-hidden", "true");
    this.#breakingNewsInnerElement.appendChild(
      this.#breakingNewsContentElementCopy,
    );
    this.#animationDurationInMs =
      this.#breakingNewsInnerElement.scrollWidth * 12;
    this.#breakingNewsElement.style.setProperty(
      "--duration",
      `${this.#animationDurationInMs}ms`,
    );
    this.#breakingNewsElement.style.setProperty(
      "--repeat",
      `${this.#animationRepeat}`,
    );
    this.#breakingNewsInnerElement.classList.add("animate");

    // Stop animation after 3 times
    this.#resetTimeoutId = setTimeout(
      this.#cancelAll,
      this.#animationDurationInMs * this.#animationRepeat + 200,
    );

    // Stop animation on interaction
    this.#breakingNewsElement.addEventListener("pointerdown", this.#cancelAll);
  };

  #stopAnimation = () => {
    if (
      !this.#breakingNewsInnerElement ||
      !this.#breakingNewsContentElementCopy
    )
      return;
    this.#breakingNewsInnerElement.classList.remove("animate");
    this.#breakingNewsInnerElement.removeChild(
      this.#breakingNewsContentElementCopy,
    );
  };

  #clearTimeout = () => {
    if (this.#resetTimeoutId !== null) {
      clearTimeout(this.#resetTimeoutId);
      this.#resetTimeoutId = null;
    }
  };

  #cancelAll = () => {
    this.#clearTimeout();
    this.#stopAnimation();
    this.#breakingNewsElement?.removeEventListener(
      "pointerdown",
      this.#cancelAll,
    );
  };
}

"customElements" in window &&
  customElements.get("ws-breaking-news") === undefined &&
  customElements.define("ws-breaking-news", BreakingNews);

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface HTMLElementTagNameMap {
    "ws-breaking-news": BreakingNews;
  }
}
